import { useCallback, useEffect, useRef, useState } from 'react';

export type FrameBox = { top: number; left: number; width: number; height: number };

/**
 * Where the preview sits inside the box the editor's floating pieces are positioned in, and how
 * big it is.
 *
 * The toolbar is `position: absolute` in the frame node, so what it needs is the iframe's offset
 * from that node - normally zero, never the node's place on the page. (Adding the page offset put
 * the toolbar one header-height too low, on top of the element it edited.) The size travels with
 * it so a toolbar, a card or the changes bar can be kept inside the preview rather than running
 * off its bottom or right edge. Followed through scrolls and resizes, because both move it.
 */
export function usePreviewFrameBox(active: boolean): {
  frameRef: (node: HTMLElement | null) => void;
  frameBox: FrameBox | null;
} {
  const nodeRef = useRef<HTMLElement | null>(null);
  const [frameBox, setFrameBox] = useState<FrameBox | null>(null);

  const measure = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    const frame = node.querySelector('iframe');
    const outer = node.getBoundingClientRect();
    const inner = frame ? frame.getBoundingClientRect() : outer;
    const next: FrameBox = {
      top: inner.top - outer.top,
      left: inner.left - outer.left,
      width: inner.width,
      height: inner.height,
    };
    setFrameBox((current) =>
      current &&
      Math.abs(current.top - next.top) < 0.5 &&
      Math.abs(current.left - next.left) < 0.5 &&
      Math.abs(current.width - next.width) < 0.5 &&
      Math.abs(current.height - next.height) < 0.5
        ? current
        : next,
    );
  }, []);

  const frameRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      if (node) measure();
    },
    [measure],
  );

  useEffect(() => {
    if (!active) {
      setFrameBox(null);
      return undefined;
    }
    measure();
    const observer = new ResizeObserver(measure);
    if (nodeRef.current) observer.observe(nodeRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, measure]);

  return { frameRef, frameBox };
}
